/**
 * GBなび 週報/月報のダミーデータ生成（デモ用）。
 *
 * バックエンドの天気判定ロジック (terasu-weather.ts) と同じ閾値で、
 * WeeklySummary / MonthlySummary / MonthlyAction を組み立てて TerasuReport に渡す。
 * 値はランダムなデモ用だが、ポジティブ回答率→天気の判定式は本番と同一。
 *
 * 実仕様との整合: 照らす(GBなび)は「週マスタを持たず weekKey から決定論的に設問を選ぶ」ため、
 * 各指標は担当週でのみ測定される (第1週=方向 / 2=推進 / 3=つながり / 4=余力)。よって —
 *  - 週報: 第1..filledWeeks週の担当指標だけが埋まる (残りは「未測定」)。
 *  - 月報: 天気グリッドは対角線 (各指標1セル。最大4/16セル)。他セルは恒久的に null。
 * 既定 (無引数) はこの疎な形を再現する。明示 weekWeather / monthGrid 指定時のみ、
 * パターンカタログ用途として全指標/全セルを埋める。
 *
 * 注: デモ専用。実データは terasuApi (週次/月次サマリ API) から取得する。
 */
import type {
  WeeklySummary,
  MonthlySummary,
  MonthlyAction,
  IndicatorResult,
} from '../api/terasuApi';

export type TerasuScenario = 'good' | 'mixed' | 'tough';

export type TerasuWeather = IndicatorResult['weather']; // 'sunny' | 'cloudy' | 'rain' | 'storm'

/** 天気→代表ポジティブ率（明示パターン生成用） */
export const RATE_FOR_WEATHER: Record<TerasuWeather, number> = {
  sunny: 0.8,
  cloudy: 0.5,
  rain: 0.3,
  storm: 0.1,
};

export interface DummyTerasuOptions {
  scenario?: TerasuScenario;
  /** 在籍者数 (既定20) */
  memberCount?: number;
  /** 今月のテーマ (既定 'dept_link')。デモでテーマ別の取り組み候補を確認する用途 */
  themeKey?: string;
  /** 週報: 指標ごとの天気を明示指定（direction/drive/connection/capacity） */
  weekWeather?: Record<string, TerasuWeather>;
  /** 月報: 指標ごとの4週天気グリッドを明示指定 */
  monthGrid?: Record<string, TerasuWeather[]>;
  /**
   * その月で何週分回答済みか (1〜4, 既定4=完了月)。
   * 実仕様 (指標は担当週でのみ測定) の疎な埋まり方を再現する。
   * filledWeeks=2 なら 方向(第1週)・推進(第2週) のみ埋まり、つながり/余力は未測定。
   */
  filledWeeks?: number;
}

export interface DummyTerasuData {
  weekly: WeeklySummary;
  monthly: MonthlySummary;
  action: MonthlyAction | null;
}

const INDICATORS = ['direction', 'drive', 'connection', 'capacity'] as const;
const INDICATOR_LABEL: Record<string, string> = {
  direction: '方向',
  drive: '推進',
  connection: 'つながり',
  capacity: '余力',
};
const WEATHER_EMOJI: Record<string, string> = {
  sunny: '☀',
  cloudy: '⛅',
  rain: '🌧',
  storm: '⚡',
};

/** ポジティブ回答率 → 天気 (terasu-weather.ts と同一閾値) */
function weatherForRate(rate: number): IndicatorResult['weather'] {
  if (rate >= 0.65) return 'sunny';
  if (rate >= 0.4) return 'cloudy';
  if (rate >= 0.2) return 'rain';
  return 'storm';
}

/** 3点尺度スコア → 色 (terasu-weather.ts と同一) */
function riskColorForScore(score: number): IndicatorResult['riskColor'] {
  if (score >= 2.6) return 'green';
  if (score >= 2.0) return 'yellow';
  return 'red';
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const jitter = (base: number, amp: number) => clamp01(base + (Math.random() * 2 - 1) * amp);

/** シナリオ×指標ごとのポジティブ回答率の基準値 */
function baseRate(scenario: TerasuScenario, indicator: string): number {
  const base: Record<TerasuScenario, number> = { good: 0.78, mixed: 0.58, tough: 0.34 };
  let r = base[scenario];
  // 「つながり」を弱点として一段下げる（V字・孤立のナラティブと整合）
  if (indicator === 'connection') r -= 0.18;
  if (indicator === 'capacity' && scenario !== 'good') r -= 0.08;
  return clamp01(r);
}

/** ポジティブ回答率と回答者数から指標結果を作る */
function makeIndicator(indicatorKey: string, rate: number, n: number): IndicatorResult {
  const positive = Math.round(rate * n);
  const negative = Math.round((1 - rate) * 0.55 * n);
  const neutral = Math.max(0, n - positive - negative);
  const total = positive + neutral + negative || 1;
  const pr = positive / total;
  const score = (positive * 3 + neutral * 2 + negative * 1) / total;
  const weather = weatherForRate(pr);
  return {
    indicatorKey,
    label: INDICATOR_LABEL[indicatorKey],
    weather,
    emoji: WEATHER_EMOJI[weather],
    positiveRate: pr,
    score: Math.round(score * 100) / 100,
    riskColor: riskColorForScore(score),
    counts: { positive, neutral, negative },
  };
}

export function generateDummyTerasuReport(opts: DummyTerasuOptions = {}): DummyTerasuData {
  const scenario = opts.scenario ?? 'mixed';
  const memberCount = Math.max(1, opts.memberCount ?? 20);
  const responseRate = scenario === 'tough' ? 0.62 : scenario === 'mixed' ? 0.78 : 0.9;
  const respondedUsers = Math.max(1, Math.round(memberCount * responseRate));

  // 実仕様: 指標は「担当週」で測定される (第1週=方向, 2=推進, 3=つながり, 4=余力)。
  // filledWeeks = その月で何週分回答済みか (既定4=完了月)。レポート対象週=最新回答週。
  const filledWeeks = Math.max(1, Math.min(4, opts.filledWeeks ?? 4));
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const mm = String(month).padStart(2, '0');
  const week = filledWeeks;
  const weekKey = `${year}-${mm}-W${week}`;

  // ---- 週次 指標: 担当週で測定される疎な形 (明示 weekWeather 指定時のみ全4指標) ----
  const indicators: WeeklySummary['indicators'] = {};
  let weakestRate = 1;
  INDICATORS.forEach((key, idx) => {
    const measured = !!opts.weekWeather || idx + 1 <= filledWeeks; // 第(idx+1)週で測定
    if (!measured) return; // 未測定 → レポートは「未測定」表示
    const rate = opts.weekWeather
      ? RATE_FOR_WEATHER[opts.weekWeather[key]]
      : jitter(baseRate(scenario, key), 0.08);
    const ind = makeIndicator(key, rate, respondedUsers);
    indicators[key] = { ...ind, weekKey: `${year}-${mm}-W${idx + 1}` };
    if (ind.positiveRate < weakestRate) weakestRate = ind.positiveRate;
  });
  // 総合天気 = 測定済み指標のうち最も弱い天気 (一番弱いところを直す思想)。未測定のみなら null。
  const overallWeather = Object.keys(indicators).length ? weatherForRate(weakestRate) : null;

  // 危険信号: 雨/雷の指標 (測定済みのみ)
  const danger = INDICATORS.filter(
    (k) => indicators[k] && ['rain', 'storm'].includes(indicators[k]!.weather)
  ).map((k) => ({
    indicatorKey: k,
    reasons: [`${INDICATOR_LABEL[k]}のポジティブ回答率が低下しています`],
    latestWeather: indicators[k]!.weather,
  }));

  const weekly: WeeklySummary = {
    weekKey,
    year,
    month,
    week,
    overallWeather,
    overallEmoji: overallWeather ? WEATHER_EMOJI[overallWeather] : null,
    indicators,
    theme: null,
    danger,
    respondedUsers,
    memberCount,
    responseRate: Math.round(responseRate * 100),
  };

  // ---- 月次: 天気グリッド (4指標 × 4週) ----
  // 実仕様: 天気グリッドは対角線 (各指標は担当週でのみ測定 → 1セルのみ)。
  // 明示 monthGrid 指定時のみ全セルを埋める (月報パターンカタログ用)。
  const grid: MonthlySummary['grid'] = {
    direction: [null, null, null, null],
    drive: [null, null, null, null],
    connection: [null, null, null, null],
    capacity: [null, null, null, null],
  };
  INDICATORS.forEach((key, idx) => {
    if (opts.monthGrid) {
      grid[key] = [...opts.monthGrid[key]];
    } else if (idx + 1 <= filledWeeks) {
      // 第(idx+1)週の対角セルのみ (他週は未測定=null)
      grid[key][idx] = weatherForRate(jitter(baseRate(scenario, key), 0.12));
    }
  });

  // ---- 月次: 部署別 (在籍7名以上のみ表示対象) ----
  const deptDefs = [
    {
      departmentId: 'dept-mfg',
      departmentName: '製造部',
      memberCount: Math.max(7, Math.round(memberCount * 0.45)),
      bias: -0.05,
    },
    { departmentId: 'dept-sales', departmentName: '営業部', memberCount: Math.max(7, Math.round(memberCount * 0.3)), bias: 0.05 },
  ];
  const byDepartment: MonthlySummary['byDepartment'] = deptDefs
    .filter((d) => d.memberCount >= 7)
    .map((d) => {
      const responded = Math.max(1, Math.round(d.memberCount * responseRate));
      const indMap: Record<string, IndicatorResult> = {};
      // 部署別も測定済み指標のみ (対角グリッドと同じ集合)
      const measuredKeys = opts.monthGrid ? INDICATORS : INDICATORS.slice(0, filledWeeks);
      for (const key of measuredKeys) {
        indMap[key] = makeIndicator(key, jitter(baseRate(scenario, key) + d.bias, 0.08), responded);
      }
      return {
        departmentId: d.departmentId,
        departmentName: d.departmentName,
        memberCount: d.memberCount,
        respondedUsers: responded,
        indicators: indMap,
      };
    });

  const themeKey = opts.themeKey ?? 'dept_link';
  const monthly: MonthlySummary = {
    year,
    month,
    grid,
    byDepartment,
    themeKey,
    theme: {
      [`theme:${themeKey}`]: makeIndicator(
        themeKey,
        jitter(baseRate(scenario, 'connection'), 0.08),
        respondedUsers
      ),
    },
    respondedUsers,
    memberCount,
    responseRate: Math.round(responseRate * 100),
  };

  // ---- 月間アクション ----
  const actionTextByScenario: Record<TerasuScenario, string> = {
    good: '今月は、うまくいっている連携の事例を1つ、朝礼で共有してみる',
    mixed: '今月は、最近話せていない社員を1人思い浮かべ、5分だけ雑談してみる',
    tough: '今月は、現場で一番忙しそうな人に「何か手伝えることある？」と一言かけてみる',
  };
  const action: MonthlyAction = {
    text: actionTextByScenario[scenario],
    executionCount: scenario === 'good' ? 2 : scenario === 'mixed' ? 1 : 0,
    phase: 1,
  };

  return { weekly, monthly, action };
}
